import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Building, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BankDetailsStepProps {
  onSubmit: (data: any) => void;
  onPrevious?: () => void;
  initialData?: any;
  shouldShowField?: (fieldPath: string) => boolean;
  isFieldRequired?: (fieldPath: string) => boolean;
  getFieldDefaultValue?: (fieldPath: string, includeWhenVisible?: boolean) => string | undefined;
}

export function BankDetailsStep({ 
  onSubmit, 
  onPrevious, 
  initialData,
  shouldShowField = () => true,
  isFieldRequired = () => true,
  getFieldDefaultValue
}: BankDetailsStepProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create dynamic schema based on conditional requirements
  const bankDetailsSchema = useMemo(() => {
    const baseSchema: any = {};

    if (shouldShowField('bankDetails.bankName')) {
      baseSchema.bankName = isFieldRequired('bankDetails.bankName')
        ? z.string().min(1, 'Bank name is required')
        : z.string().optional();
    }

    if (shouldShowField('bankDetails.routingNumber')) {
      baseSchema.routingNumber = isFieldRequired('bankDetails.routingNumber')
        ? z.string()
            .min(9, 'Routing number must be 9 digits')
            .max(9, 'Routing number must be 9 digits')
            .regex(/^\d+$/, 'Routing number must contain only numbers')
        : z.string().optional();
    }

    if (shouldShowField('bankDetails.accountNumber')) {
      baseSchema.accountNumber = isFieldRequired('bankDetails.accountNumber')
        ? z.string().min(6, 'Account number must be at least 6 characters')
        : z.string().min(6, 'Account number must be at least 6 characters').optional();
    }

    // Confirm account number always matches the logic if account number is shown
    if (shouldShowField('bankDetails.accountNumber')) {
      baseSchema.confirmAccountNumber = z
        .string()
        .min(6, 'Confirmation must match the account number length')
        .optional();
    }

    if (shouldShowField('bankDetails.accountType')) {
      baseSchema.accountType = z.enum(['checking', 'savings'], {
        required_error: 'Select an account type',
      });
    }

    const schema = z.object(baseSchema);

    // Add refinement for matching account numbers if both are shown and required
    if (shouldShowField('bankDetails.accountNumber') && shouldShowField('bankDetails.accountNumber')) {
      return schema.refine(
        (data) => {
          if (!data.accountNumber || !data.confirmAccountNumber) return true;
          return data.accountNumber === data.confirmAccountNumber;
        },
        {
          message: 'Account numbers must match',
          path: ['confirmAccountNumber']
        }
      );
    }

    return schema;
  }, [shouldShowField, isFieldRequired]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(bankDetailsSchema),
    defaultValues: {
      bankName:
        initialData?.bankDetails?.bankName ||
        initialData?.bankName ||
        (!shouldShowField('bankDetails.bankName') ? getFieldDefaultValue?.('bankDetails.bankName') : '') ||
        '',
      routingNumber:
        initialData?.bankDetails?.routingNumber ||
        initialData?.routingNumber ||
        (!shouldShowField('bankDetails.routingNumber') ? getFieldDefaultValue?.('bankDetails.routingNumber') : '') ||
        '',
      accountNumber:
        initialData?.bankDetails?.accountNumber ||
        initialData?.accountNumber ||
        (!shouldShowField('bankDetails.accountNumber') ? getFieldDefaultValue?.('bankDetails.accountNumber') : '') ||
        '',
      confirmAccountNumber:
        initialData?.bankDetails?.accountNumber ||
        initialData?.accountNumber ||
        '',
      accountType:
        initialData?.bankDetails?.accountType ||
        initialData?.accountType ||
        getFieldDefaultValue?.('bankDetails.accountType', true) ||
        'checking',
    },
    mode: 'onBlur',
  });

  const onFormSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      setServerError(null);
      
      // Remove confirmAccountNumber before saving
      const { confirmAccountNumber, ...bankData } = data;

      // Apply default values for hidden fields
      const fieldList = ['bankName', 'routingNumber', 'accountNumber', 'accountType'] as const;
      fieldList.forEach((field) => {
        const path = `bankDetails.${field}`;
        if (!shouldShowField(path)) {
          const defVal = getFieldDefaultValue?.(path);
          if (defVal !== undefined) (bankData as any)[field] = defVal;
        }
      });
      await onSubmit({ bankDetails: bankData });
    } catch (error) {
      console.error('Submission error:', error);
      setServerError('An error occurred while saving your information');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="h-5 w-5" />
          Bank Details
        </CardTitle>
        <CardDescription>
          We need this information to know where to deposit your funds.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {serverError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shouldShowField('bankDetails.bankName') && (
            <div className="space-y-2">
              <Label htmlFor="bankName">
                Bank Name {isFieldRequired('bankDetails.bankName') && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="bankName"
                {...register('bankName')}
                placeholder="e.g., Chase Bank, Bank of America"
              />
              {errors.bankName && (
                <p className="text-sm text-red-600">{errors.bankName.message as string}</p>
              )}
            </div>
          )}

          {shouldShowField('bankDetails.routingNumber') && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="routingNumber">
                  Routing Number {isFieldRequired('bankDetails.routingNumber') && <span className="text-red-500">*</span>}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger type="button">
                      <Info className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>The 9-digit routing number is typically found at the bottom left of your checks</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="routingNumber"
                {...register('routingNumber')}
                placeholder="123456789"
                maxLength={9}
              />
              {errors.routingNumber && (
                <p className="text-sm text-red-600">{errors.routingNumber.message as string}</p>
              )}
            </div>
          )}
        </div>

        {shouldShowField('bankDetails.accountNumber') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="accountNumber">
                  Account Number {isFieldRequired('bankDetails.accountNumber') && <span className="text-red-500">*</span>}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger type="button">
                      <Info className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Your account number is typically found at the bottom center of your checks</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="accountNumber"
                {...register('accountNumber')}
                type="password"
                placeholder="Enter your account number"
                autoComplete="off"
              />
              {errors.accountNumber && (
                <p className="text-sm text-red-600">{errors.accountNumber.message as string}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmAccountNumber">
                Confirm Account Number {isFieldRequired('bankDetails.accountNumber') && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="confirmAccountNumber"
                {...register('confirmAccountNumber')}
                type="password"
                placeholder="Re-enter your account number"
                autoComplete="off"
              />
              {errors.confirmAccountNumber && (
                <p className="text-sm text-red-600">{errors.confirmAccountNumber.message as string}</p>
              )}
            </div>
          </div>
        )}

        {/* Account Type */}
        {shouldShowField('bankDetails.accountType') && (
          <div className="space-y-2">
            <Label htmlFor="accountType">
              Account Type {isFieldRequired('bankDetails.accountType') && <span className="text-red-500">*</span>}
            </Label>
            <select
              id="accountType"
              {...register('accountType')}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
            {errors.accountType && (
              <p className="text-sm text-red-600">{errors.accountType.message as string}</p>
            )}
          </div>
        )}

        {/* Additional Information */}
        {/* Form Actions */}
        <div className="flex justify-between pt-6">
          {onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting} className="ml-auto">
            {isSubmitting ? 'Saving...' : 'Save & Continue'}
          </Button>
        </div>
      </CardContent>
    </form>
  );
}
